import { useEffect, useState } from "react";
import { api, frDate, openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, FileDown } from "lucide-react";

const empty = {
  reference: "", volunteer_name: "", birth_date: "", address: "",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date().toISOString().slice(0, 10),
  hours: 0, activities: "",
  issue_date: new Date().toISOString().slice(0, 10),
};

export default function Attestations() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/certificates").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.volunteer_name) { toast.error("Nom requis"); return; }
    try {
      const payload = { ...form, hours: Number(form.hours) || 0,
        reference: form.reference || `ATT-${Date.now().toString().slice(-6)}` };
      if (edit) await api.put(`/certificates/${edit.id}`, payload);
      else await api.post("/certificates", payload);
      toast.success(edit ? "Attestation modifiée." : "Attestation créée.");
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const remove = async (c) => {
    if (!window.confirm("Supprimer cette attestation ?")) return;
    await api.delete(`/certificates/${c.id}`); load();
  };

  return (
    <div data-testid="attestations-page">
      <PageHeader
        eyebrow="Documents"
        title="Attestations de bénévolat"
        description="Valorisez l'engagement des bénévoles par une attestation officielle téléchargeable en PDF."
        actions={
          <Button data-testid="new-cert-btn" onClick={() => { setEdit(null); setForm({ ...empty, reference: `ATT-${Date.now().toString().slice(-6)}` }); setOpen(true); }} className="bg-brand-orange hover:bg-[#D04A06] text-white">
            <Plus size={16} className="mr-1" /> Nouvelle attestation
          </Button>
        }
      />

      <div className="bg-white border border-brand-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 small-caps">
            <tr>
              <th className="text-left py-3 px-4">Référence</th>
              <th className="text-left py-3 px-4">Bénévole</th>
              <th className="text-left py-3 px-4">Période</th>
              <th className="text-left py-3 px-4">Heures</th>
              <th className="text-left py-3 px-4">Émise le</th>
              <th className="text-right py-3 px-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-gray-400">Aucune attestation.</td></tr>}
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-brand-border hover:bg-gray-50">
                <td className="py-3 px-4 font-mono text-xs">{c.reference}</td>
                <td className="py-3 px-4 font-medium text-gray-900">{c.volunteer_name}</td>
                <td className="py-3 px-4 text-gray-700 text-xs">Du {frDate(c.start_date)}<br/>au {frDate(c.end_date)}</td>
                <td className="py-3 px-4"><Badge className="bg-brand-green text-white">{c.hours} h</Badge></td>
                <td className="py-3 px-4 text-gray-700">{frDate(c.issue_date)}</td>
                <td className="py-3 px-4 text-right space-x-1">
                  <Button size="sm" variant="outline" onClick={() => openPdf(`/certificates/${c.id}/pdf`)}><FileDown size={14} className="mr-1" />PDF</Button>
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(c); setForm({...empty, ...c}); setOpen(true); }}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(c)}><Trash2 size={14} className="text-red-500" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-brand-navy">{edit ? "Modifier" : "Nouvelle attestation"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div><Label>Référence</Label><Input value={form.reference} onChange={(e) => setForm({...form, reference: e.target.value})} /></div>
            <div><Label>Date d'émission</Label><Input type="date" value={form.issue_date} onChange={(e) => setForm({...form, issue_date: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Nom du (de la) bénévole *</Label><Input value={form.volunteer_name} onChange={(e) => setForm({...form, volunteer_name: e.target.value})} /></div>
            <div><Label>Date de naissance</Label><Input type="date" value={form.birth_date || ""} onChange={(e) => setForm({...form, birth_date: e.target.value})} /></div>
            <div><Label>Heures de bénévolat</Label><Input type="number" step="0.5" value={form.hours} onChange={(e) => setForm({...form, hours: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Adresse</Label><Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} /></div>
            <div><Label>Début d'activité</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({...form, start_date: e.target.value})} /></div>
            <div><Label>Fin d'activité</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({...form, end_date: e.target.value})} /></div>
            <div className="md:col-span-2"><Label>Activités réalisées</Label><Textarea rows={3} value={form.activities} onChange={(e) => setForm({...form, activities: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} className="bg-brand-orange hover:bg-[#D04A06] text-white">{edit ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
